import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Table,
  Typography,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  InputNumber,
  message,
  Descriptions,
  Alert,
  Dropdown,
  Tooltip,
  Tag,
  Upload,
  Rate,
  Switch,
} from 'antd';
import type { MenuProps, TableProps, UploadFile } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  UserAddOutlined,
  FileTextOutlined,
  AimOutlined,
  MoreOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  supabase,
  resolveTripGeopointsStrict,
  TripGeocodeError,
  backfillTripGeopoints,
  parseGeoPoint,
  findTripAssignmentConflicts,
  isDriverBlockedByFullDayEscort,
  defaultEndTimeFromPickup,
  canEditTrip,
  canCancelTrip,
  getAllowedNextStatuses,
  resolveTripStatusAfterAssign,
  shouldDemoteToScheduled,
  canTransition,
  useRealtimeChannel,
  formatDate,
  formatDateTime,
  formatTime,
  generateTripDocument,
  assignPlatformTripToOwner,
  formatTripSourceLabel,
  formatAssignmentStatusLabel,
  usePortalPermissions,
  useAuthUser,
  useFleetOperator,
  parseUserRole,
  isFleetOwnerRole,
  isDispatchBlockedByExpiry,
  parseComplianceDispatchError,
  computeTripRouteEta,
  fetchComposeTripNotesFromIds,
  uploadFile,
  generateUUID,
  getSignedUrl,
  routeDriveSourceI18nKey,
  routeDriveSourceTagColor,
  formatRouteDriveKm,
} from '@ubus/shared';
import type { Trip, TripStatus, TripFeedback, AddressCoordinates, FleetOperatorRecommendation } from '@ubus/shared';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AddressField } from '@ubus/shared';
import EntityDrawer from '../components/EntityDrawer';
import AdminListPage from '../components/AdminListPage';
import StatusTag from '../components/StatusTag';
import { TableRowActions } from '../components/TableRowActions';
import DispatchRecommendationList from '../components/DispatchRecommendationList';
import FleetOperatorRecommendationList from '../components/FleetOperatorRecommendationList';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import {
  useTableColumnPins,
  type ColumnPinSide,
} from '../hooks/useTableColumnPins';
import { formatTripBrief } from '../utils/formatTripBrief';
import ImageEditModal from '../components/ImageEditModal';
import { localeConfirm } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';
import { isPdfFile, pdfFileToJpegFiles } from '../utils/pdfToImages';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const TRIPS_COLUMN_PIN_DEFAULTS: Record<string, ColumnPinSide> = {
  trip_number: 'left',
  actions: 'right',
};

interface OrgOption {
  id: string;
  name: string;
}

interface VehicleOption {
  id: string;
  fleet_number: string;
  model: string;
  preferred_driver_id?: string | null;
  registration_expiry?: string | null;
  fleet_operator_id?: string | null;
}

interface DriverOption {
  id: string;
  name: string;
  license_expiry?: string | null;
  fleet_operator_id?: string | null;
}

interface FleetOperatorOption {
  id: string;
  name: string;
  status: string;
}

type TripWithRelations = Omit<Trip, 'vehicles' | 'drivers' | 'organizations'> & {
  vehicles?: { fleet_number: string; model: string } | null;
  drivers?: { name: string } | null;
  organizations?: { name: string } | null;
  fleet_operators?: { id: string; name: string } | null;
  jobs?: { id: string; job_number?: string | null; title?: string | null } | null;
  attachment_urls?: string[] | null;
};

function Trips() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePortalPermissions();
  const canEditTrips = hasPermission('trips.edit');
  const canOpenDailySchedule = hasPermission('daily_schedule.view');
  const authUser = useAuthUser();
  const isOwnerMode = isFleetOwnerRole(parseUserRole(authUser));
  const { operatorId, loading: fleetLoading } = useFleetOperator();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFromUrl = searchParams.get('date');
  const initialDateRange = (() => {
    if (dateFromUrl && dayjs(dateFromUrl, 'YYYY-MM-DD', true).isValid()) {
      const d = dayjs(dateFromUrl);
      return [d.startOf('day'), d.startOf('day')] as [Dayjs, Dayjs];
    }
    const start = dayjs().startOf('day');
    return [start, start.add(2, 'day')] as [Dayjs, Dayjs];
  })();
  const [trips, setTrips] = useState<TripWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [editingTrip, setEditingTrip] = useState<TripWithRelations | null>(null);
  const [assignTrip, setAssignTrip] = useState<TripWithRelations | null>(null);
  const [ownerAssignModalVisible, setOwnerAssignModalVisible] = useState(false);
  const [ownerAssignTrip, setOwnerAssignTrip] = useState<TripWithRelations | null>(null);
  const [detailTrip, setDetailTrip] = useState<TripWithRelations | null>(null);
  const [detailFeedback, setDetailFeedback] = useState<TripFeedback[]>([]);
  const [detailFeedbackLoading, setDetailFeedbackLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(initialDateRange);
  const [driverFilter, setDriverFilter] = useState<string | undefined>(undefined);
  const [orgFilter, setOrgFilter] = useState<string | undefined>(undefined);
  const [fleetFilter, setFleetFilter] = useState<string | undefined>(undefined);
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());
  const highlightTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const {
    pagination,
    onTableChange,
    rowClassName: stabilityRowClassName,
    afterMutation,
  } = useAdminTableStability();
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [detailStatus, setDetailStatus] = useState<TripStatus | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [generatingItinerary, setGeneratingItinerary] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [pickupCoords, setPickupCoords] = useState<AddressCoordinates | null>(null);
  const [dropoffCoords, setDropoffCoords] = useState<AddressCoordinates | null>(null);
  const [savingTrip, setSavingTrip] = useState(false);
  const [missingGeopointCount, setMissingGeopointCount] = useState(0);
  const [backfillingGeopoints, setBackfillingGeopoints] = useState(false);
  const [assignAttachmentFileList, setAssignAttachmentFileList] = useState<UploadFile[]>([]);
  const [assignExistingPaths, setAssignExistingPaths] = useState<string[]>([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [imageEditFile, setImageEditFile] = useState<File | null>(null);
  const [imageEditOpen, setImageEditOpen] = useState(false);
  const [pdfConverting, setPdfConverting] = useState(false);
  const imageEditQueueRef = useRef<File[]>([]);

  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [fleetOperators, setFleetOperators] = useState<FleetOperatorOption[]>([]);
  const assignFleetOperatorId = Form.useWatch('fleet_operator_id', assignForm) as
    | string
    | undefined;

  useEffect(() => {
    const raw = searchParams.get('date');
    if (raw && dayjs(raw, 'YYYY-MM-DD', true).isValid()) {
      const d = dayjs(raw, 'YYYY-MM-DD');
      setDateRange((prev) => {
        if (prev?.[0]?.isSame(d, 'day') && prev?.[1]?.isSame(d, 'day')) return prev;
        return [d, d];
      });
    }
  }, [searchParams]);

  const flashRow = useCallback((id: string) => {
    setHighlightedIds((prev) => new Set(prev).add(id));
    const existing = highlightTimers.current.get(id);
    if (existing) clearTimeout(existing);
    highlightTimers.current.set(
      id,
      setTimeout(() => {
        setHighlightedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        highlightTimers.current.delete(id);
      }, 2500)
    );
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [orgsRes, vehiclesRes, driversRes, fleetsRes] = await Promise.all([
        supabase.from('organizations').select('id, name').order('name'),
        supabase
          .from('vehicles')
          .select('id, fleet_number, model, preferred_driver_id, registration_expiry, fleet_operator_id')
          .eq('status', 'active')
          .order('fleet_number'),
        supabase
          .from('drivers')
          .select('id, name, license_expiry, fleet_operator_id')
          .eq('status', 'active')
          .order('name'),
        supabase
          .from('fleet_operators')
          .select('id, name, status')
          .eq('status', 'active')
          .order('name'),
      ]);

      if (orgsRes.data) setOrganizations(orgsRes.data);
      if (vehiclesRes.data) setVehicles(vehiclesRes.data);
      if (driversRes.data) setDrivers(driversRes.data);
      if (fleetsRes.data) setFleetOperators(fleetsRes.data);
    } catch (err) {
      console.error('Failed to fetch dropdown data:', err);
    }
  };

  const fetchMissingGeopointCount = useCallback(async () => {
    let q = supabase
      .from('trips')
      .select('id', { count: 'exact', head: true })
      .or('pickup_geopoint.is.null,dropoff_geopoint.is.null');
    if (isOwnerMode && operatorId) {
      q = q.eq('fleet_operator_id', operatorId);
    }
    const { count, error } = await q;

    if (!error && count !== null) {
      setMissingGeopointCount(count);
    }
  }, [isOwnerMode, operatorId]);

  const fetchTrips = useCallback(async () => {
    if (isOwnerMode && (fleetLoading || !operatorId)) {
      setTrips([]);
      setLoading(fleetLoading);
      return;
    }
    setLoading(true);
    try {
      let query = supabase
        .from('trips')
        .select('*, vehicles(*), drivers(*), organizations(*), fleet_operators(id, name), jobs(id, job_number, title)')
        .order('date', { ascending: false });

      if (isOwnerMode && operatorId) {
        query = query.eq('fleet_operator_id', operatorId);
      }

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }
      if (dateRange?.[0]) {
        query = query.gte('date', dateRange[0].format('YYYY-MM-DD'));
      }
      if (dateRange?.[1]) {
        query = query.lte('date', dateRange[1].format('YYYY-MM-DD'));
      }
      if (driverFilter) {
        query = query.eq('driver_id', driverFilter);
      }
      if (orgFilter) {
        query = query.eq('org_id', orgFilter);
      }
      if (!isOwnerMode && fleetFilter) {
        query = query.eq('fleet_operator_id', fleetFilter);
      }

      const { data, error } = await query;

      if (error) {
        message.error(t('trips.messages.loadFailed'));
      } else {
        setTrips((data as TripWithRelations[]) || []);
      }
      await fetchMissingGeopointCount();
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    dateRange,
    driverFilter,
    orgFilter,
    fleetFilter,
    fetchMissingGeopointCount,
    isOwnerMode,
    operatorId,
    fleetLoading,
    t,
  ]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  useRealtimeChannel(
    'trips-admin-realtime',
    [
      {
        event: '*',
        table: 'trips',
        callback: (payload) => {
          const updatedTrip = payload.new as TripWithRelations;
          const oldTrip = payload.old as TripWithRelations;

          if (payload.eventType === 'INSERT') {
            void fetchTrips();
            if (updatedTrip?.id) flashRow(updatedTrip.id);
          } else if (payload.eventType === 'UPDATE') {
            setTrips((prev) =>
              prev.map((t) => (t.id === updatedTrip.id ? { ...t, ...updatedTrip } : t))
            );
            if (updatedTrip?.id) flashRow(updatedTrip.id);
            if (detailTrip?.id === updatedTrip.id) {
              setDetailTrip((prev) => (prev ? { ...prev, ...updatedTrip } : prev));
              setDetailStatus(updatedTrip.status as TripStatus);
            }
          } else if (payload.eventType === 'DELETE') {
            setTrips((prev) => prev.filter((t) => t.id !== oldTrip.id));
          }
        },
      },
    ],
    [fetchTrips, flashRow, detailTrip?.id]
  );

  useEffect(() => {
    return () => {
      highlightTimers.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const handleCreate = () => {
    setEditingTrip(null);
    setPickupCoords(null);
    setDropoffCoords(null);
    form.resetFields();
    form.setFieldsValue({ status: 'scheduled', driver_full_day_escort: false });
    setModalVisible(true);
  };

  const handleEditOrView = (record: TripWithRelations) => {
    if (canEditTrip(record.status as TripStatus)) {
      setEditingTrip(record);
      setPickupCoords(record.pickup_geopoint ? parseGeoPoint(record.pickup_geopoint) : null);
      setDropoffCoords(record.dropoff_geopoint ? parseGeoPoint(record.dropoff_geopoint) : null);
      form.setFieldsValue({
        ...record,
        date: record.date ? dayjs(record.date) : null,
        pickup_time: record.pickup_time ? dayjs(record.pickup_time, 'HH:mm:ss') : null,
        end_time: record.end_time ? dayjs(record.end_time, 'HH:mm:ss') : null,
        driver_full_day_escort: Boolean(record.driver_full_day_escort),
      });
      setModalVisible(true);
    } else {
      openDetail(record);
    }
  };

  const handleBackfillGeopoints = async () => {
    setBackfillingGeopoints(true);
    try {
      const { data, error } = await supabase
        .from('trips')
        .select('id, pickup_address, dropoff_address, pickup_geopoint, dropoff_geopoint')
        .or('pickup_geopoint.is.null,dropoff_geopoint.is.null')
        .limit(50);

      if (error) throw error;
      if (!data?.length) {
        message.info(t('trips.messages.allGeocoded'));
        return;
      }

      let fixed = 0;
      let failed = 0;
      for (const trip of data) {
        try {
          const updated = await backfillTripGeopoints(trip);
          if (updated) fixed += 1;
        } catch {
          failed += 1;
        }
      }

      message.success(
        failed
          ? t('trips.messages.geocodedWithFailures', { fixed, failed })
          : t('trips.messages.geocoded', { fixed })
      );
      fetchTrips();
    } catch (err: unknown) {
      message.error(t('trips.messages.backfillFailed'));
    } finally {
      setBackfillingGeopoints(false);
    }
  };

  const openDetail = (record: TripWithRelations) => {
    setDetailTrip(record);
    setDetailStatus(record.status as TripStatus);
    setCancelReason('');
  };

  const fetchDetailFeedback = useCallback(async (tripId: string) => {
    setDetailFeedbackLoading(true);
    try {
      const { data, error } = await supabase
        .from('trip_feedback')
        .select('*')
        .eq('trip_id', tripId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDetailFeedback((data || []) as TripFeedback[]);
    } catch (err: unknown) {
      setDetailFeedback([]);
      message.error(err instanceof Error ? err.message : t('trips.feedback.loadFailed'));
    } finally {
      setDetailFeedbackLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!detailTrip?.id) {
      setDetailFeedback([]);
      return;
    }
    void fetchDetailFeedback(detailTrip.id);
  }, [detailTrip?.id, fetchDetailFeedback]);

  const markDetailFeedbackReviewed = async (id: string) => {
    const { error } = await supabase
      .from('trip_feedback')
      .update({ status: 'reviewed' })
      .eq('id', id);
    if (error) {
      message.error(error.message);
      return;
    }
    message.success(t('trips.feedback.markedReviewed'));
    if (detailTrip?.id) await fetchDetailFeedback(detailTrip.id);
  };

  // Deep link from Schedule Overview Trip Peek: ?trip=
  useEffect(() => {
    const tripId = searchParams.get('trip');
    if (!tripId || loading) return;
    const found = trips.find((tr) => tr.id === tripId);
    if (found) {
      openDetail(found);
      flashRow(tripId);
      const next = new URLSearchParams(searchParams);
      next.delete('trip');
      setSearchParams(next, { replace: true });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('trips')
        .select('*, vehicles(*), drivers(*), organizations(*), jobs(id, job_number, title)')
        .eq('id', tripId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      openDetail(data as TripWithRelations);
      flashRow(tripId);
      const next = new URLSearchParams(searchParams);
      next.delete('trip');
      setSearchParams(next, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when trips/url ready
  }, [searchParams, trips, loading, flashRow, setSearchParams]);

  const assignFleetVehicles = useMemo(
    () =>
      assignFleetOperatorId
        ? vehicles.filter((v) => v.fleet_operator_id === assignFleetOperatorId)
        : [],
    [assignFleetOperatorId, vehicles]
  );

  const assignFleetDrivers = useMemo(
    () =>
      assignFleetOperatorId
        ? drivers.filter((d) => d.fleet_operator_id === assignFleetOperatorId)
        : [],
    [assignFleetOperatorId, drivers]
  );

  const assignAllowedVehicleIds = useMemo(
    () => assignFleetVehicles.map((v) => v.id),
    [assignFleetVehicles]
  );

  const handleAssign = (record: TripWithRelations) => {
    setAssignTrip(record);
    const vehicle = vehicles.find((v) => v.id === record.vehicle_id);
    const fleetOperatorId = isOwnerMode
      ? operatorId ?? undefined
      : vehicle?.fleet_operator_id || record.fleet_operator_id || undefined;
    assignForm.setFieldsValue({
      fleet_operator_id: fleetOperatorId,
      vehicle_id: record.vehicle_id,
      driver_id: record.driver_id || vehicle?.preferred_driver_id || undefined,
      special_notes: record.special_notes ?? '',
    });
    const paths = (record as TripWithRelations & { attachment_urls?: string[] | null })
      .attachment_urls ?? [];
    setAssignExistingPaths(paths);
    setAssignAttachmentFileList(
      paths.map((path, i) => ({
        uid: `existing-${i}`,
        name: path.split('/').pop() || `image-${i + 1}`,
        status: 'done',
        url: undefined,
      }))
    );
    setAssignModalVisible(true);

    void (async () => {
      const existing = (record.special_notes ?? '').trim();
      if (!existing) {
        const composed = await fetchComposeTripNotesFromIds(record.job_id, record.job_leg_id);
        if (composed) {
          assignForm.setFieldsValue({ special_notes: composed });
        }
      }
      const withUrls: UploadFile[] = [];
      for (let i = 0; i < paths.length; i++) {
        const signed = await getSignedUrl('uploads', paths[i], 3600);
        withUrls.push({
          uid: `existing-${i}`,
          name: paths[i].split('/').pop() || `image-${i + 1}`,
          status: 'done',
          url: signed ?? undefined,
          thumbUrl: signed ?? undefined,
        });
      }
      if (withUrls.length) setAssignAttachmentFileList(withUrls);
    })();
  };

  const startImageEditQueue = (files: File[]) => {
    imageEditQueueRef.current = [...files];
    const next = imageEditQueueRef.current.shift() ?? null;
    if (next) {
      setImageEditFile(next);
      setImageEditOpen(true);
    }
  };

  const advanceImageEditQueue = () => {
    const next = imageEditQueueRef.current.shift() ?? null;
    if (next) {
      setImageEditFile(next);
      setImageEditOpen(true);
      return;
    }
    setImageEditOpen(false);
    setImageEditFile(null);
  };

  const cancelImageEditQueue = () => {
    imageEditQueueRef.current = [];
    setImageEditOpen(false);
    setImageEditFile(null);
  };

  const handleAssignAttachmentPick = async (file: File) => {
    const slots = Math.max(0, 9 - assignAttachmentFileList.length);
    if (slots <= 0) {
      message.error(t('trips.messages.attachmentLimit'));
      return;
    }

    if (isPdfFile(file)) {
      if (file.size > 10 * 1024 * 1024) {
        message.error(t('trips.messages.attachmentPdfSize'));
        return;
      }
      setPdfConverting(true);
      const hide = message.loading(t('trips.messages.attachmentPdfConverting'), 0);
      try {
        const { files: pages, totalPages } = await pdfFileToJpegFiles(file, { maxPages: slots });
        if (!pages.length) {
          message.error(t('trips.messages.attachmentPdfEmpty'));
          return;
        }
        if (totalPages > pages.length) {
          message.info(
            t('trips.messages.attachmentPdfTruncated', {
              count: pages.length,
              total: totalPages,
            })
          );
        }
        startImageEditQueue(pages);
      } catch (err) {
        console.error('[pdfToImages]', err);
        const detail = err instanceof Error && err.message ? err.message : '';
        message.error(
          detail && detail !== 'PDF_EMPTY'
            ? t('trips.messages.attachmentPdfFailedDetail', { message: detail })
            : t('trips.messages.attachmentPdfFailed')
        );
      } finally {
        hide();
        setPdfConverting(false);
      }
      return;
    }

    const okType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!okType) {
      message.error(t('trips.messages.attachmentType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error(t('trips.messages.attachmentSize'));
      return;
    }
    startImageEditQueue([file]);
  };

  const handleAssignToOwner = (record: TripWithRelations) => {
    if (isOwnerMode) return;
    setOwnerAssignTrip(record);
    setOwnerAssignModalVisible(true);
  };

  const handleOwnerAssignSelect = async (rec: FleetOperatorRecommendation) => {
    if (!ownerAssignTrip) return;
    try {
      await assignPlatformTripToOwner({
        tripId: ownerAssignTrip.id,
        fleetOperatorId: rec.fleet_operator_id,
        vehicleId: rec.vehicle_id,
        driverId: rec.driver_id,
      });
      message.success(t('trips.messages.assignedToOwner', { name: rec.operator_name }));
      setOwnerAssignModalVisible(false);
      const tripId = ownerAssignTrip.id;
      setOwnerAssignTrip(null);
      await afterMutation(tripId, fetchTrips);
    } catch (err: unknown) {
      message.error(t('trips.messages.assignOwnerFailed'));
    }
  };

  const handleDelete = (id: string) => {
    localeConfirm(t, {
      title: t('trips.messages.deleteTitle'),
      content: t('trips.messages.deleteContent'),
      okText: t('common.delete'),
      okType: 'danger',
      onOk: async () => {
        const { error } = await supabase.from('trips').delete().eq('id', id);

        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
        } else {
          message.success(t('trips.messages.deleted'));
          fetchTrips();
        }
      },
    });
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSavingTrip(true);
    try {
      const geopoints = await resolveTripGeopointsStrict(
        {
          address: String(values.pickup_address),
          lat: pickupCoords?.lat,
          lng: pickupCoords?.lng,
        },
        {
          address: String(values.dropoff_address),
          lat: dropoffCoords?.lat,
          lng: dropoffCoords?.lng,
        }
      );

      const tripDate = values.date ? (values.date as Dayjs).format('YYYY-MM-DD') : null;
      const pickupTimeStr = values.pickup_time
        ? (values.pickup_time as Dayjs).format('HH:mm:ss')
        : null;
      const endTimeStr = values.end_time
        ? (values.end_time as Dayjs).format('HH:mm:ss')
        : pickupTimeStr
          ? defaultEndTimeFromPickup(pickupTimeStr)
          : null;

      const tripData: Record<string, unknown> = {
        org_id: values.org_id ?? null,
        contact_person:
          ((values.contact_person as string | undefined) ?? '').trim() || null,
        contact_phone:
          ((values.contact_phone as string | undefined) ?? '').trim() || null,
        date: tripDate,
        pickup_time: pickupTimeStr,
        end_time: endTimeStr,
        pickup_address: geopoints.pickup_address,
        dropoff_address: geopoints.dropoff_address,
        pickup_geopoint: geopoints.pickup_geopoint,
        dropoff_geopoint: geopoints.dropoff_geopoint,
        passenger_count: values.passenger_count,
        special_notes: values.special_notes,
        status: 'scheduled',
        driver_full_day_escort: Boolean(values.driver_full_day_escort),
      };

      if (isOwnerMode) {
        if (!operatorId) {
          message.error(t('trips.messages.saveFailed'));
          return;
        }
        tripData.source = 'owner';
        tripData.fleet_operator_id = operatorId;
        tripData.assignment_status = 'n/a';
      }

      try {
        const eta = await computeTripRouteEta({
          pickupGeopoint: geopoints.pickup_geopoint,
          dropoffGeopoint: geopoints.dropoff_geopoint,
          pickupTime: pickupTimeStr,
          date: tripDate,
        });
        Object.assign(tripData, eta);
      } catch (etaErr) {
        console.warn('computeTripRouteEta failed:', etaErr);
        Object.assign(tripData, {
          route_drive_minutes: null,
          route_drive_distance_meters: null,
          estimated_dropoff_time: null,
          route_drive_source: 'unavailable',
          route_drive_updated_at: new Date().toISOString(),
        });
      }

      if (editingTrip) {
        const tripId = editingTrip.id;
        const { error } = await supabase
          .from('trips')
          .update(tripData)
          .eq('id', tripId);

        if (error) {
          message.error(t('trips.messages.saveFailed'));
        } else {
          message.success(t('trips.messages.updated'));
          setModalVisible(false);
          await afterMutation(tripId, fetchTrips);
        }
      } else {
        const { data: created, error } = await supabase
          .from('trips')
          .insert([tripData])
          .select('id')
          .single();

        if (error) {
          message.error(t('trips.messages.saveFailed'));
        } else {
          message.success(t('trips.messages.created'));
          setModalVisible(false);
          await afterMutation(created?.id, fetchTrips);
        }
      }
    } catch (err: unknown) {
      if (err instanceof TripGeocodeError) {
        message.error(t('trips.messages.saveFailed'));
      } else {
        message.error(t('trips.messages.saveFailed'));
      }
    } finally {
      setSavingTrip(false);
    }
  };

  const handleAssignSubmit = async (values: {
    fleet_operator_id?: string;
    vehicle_id?: string;
    driver_id?: string;
    special_notes?: string;
  }) => {
    if (!assignTrip) return;

    try {
      if (!values.fleet_operator_id) {
        message.error(t('trips.validation.fleetRequired'));
        return;
      }

      setAssignSaving(true);

      const tripDate = assignTrip.date;
      const vehicle = values.vehicle_id
        ? vehicles.find((v) => v.id === values.vehicle_id)
        : undefined;
      const driver = values.driver_id
        ? drivers.find((d) => d.id === values.driver_id)
        : undefined;

      if (vehicle && vehicle.fleet_operator_id !== values.fleet_operator_id) {
        message.error(t('trips.validation.vehicleFleetMismatch'));
        return;
      }
      if (driver && driver.fleet_operator_id !== values.fleet_operator_id) {
        message.error(t('trips.validation.driverFleetMismatch'));
        return;
      }

      if (
        vehicle &&
        isDispatchBlockedByExpiry(vehicle.registration_expiry, tripDate)
      ) {
        message.error(
          t('trips.messages.vehicleRegistrationExpired', {
            date: vehicle.registration_expiry,
            tripDate,
          })
        );
        return;
      }
      if (driver && isDispatchBlockedByExpiry(driver.license_expiry, tripDate)) {
        message.error(
          t('trips.messages.driverLicenseExpired', {
            date: driver.license_expiry,
            tripDate,
          })
        );
        return;
      }

      const conflicts = await findTripAssignmentConflicts({
        date: tripDate,
        vehicleId: values.vehicle_id,
        driverId: values.driver_id,
        excludeTripId: assignTrip.id,
        pickupTime: assignTrip.pickup_time,
        endTime: assignTrip.end_time,
      });

      if (conflicts.length > 0) {
        message.error(t('trips.messages.assignmentConflict'));
        return;
      }

      if (values.driver_id) {
        const escortBlocked = await isDriverBlockedByFullDayEscort({
          driverId: values.driver_id,
          date: tripDate,
          excludeTripId: assignTrip.id,
          assigningEscort: Boolean(assignTrip.driver_full_day_escort),
        });
        if (escortBlocked) {
          message.error(t('trips.messages.fullDayEscortConflict'));
          return;
        }
      }

      const retained = assignAttachmentFileList
        .filter((f) => typeof f.uid === 'string' && String(f.uid).startsWith('existing-'))
        .map((f) => {
          const i = Number(String(f.uid).replace('existing-', ''));
          return Number.isFinite(i) ? assignExistingPaths[i] : undefined;
        })
        .filter((p): p is string => Boolean(p));

      const newPaths: string[] = [];
      for (const f of assignAttachmentFileList) {
        const file = f.originFileObj;
        if (!file) continue;
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const path = await uploadFile(
          'uploads',
          `trip-attachments/${assignTrip.id}/${generateUUID()}.${ext}`,
          file
        );
        if (path) newPaths.push(path);
        else {
          message.error(t('trips.messages.attachmentUploadFailed'));
          return;
        }
      }

      const attachmentUrls = [...retained, ...newPaths];

      const vehicleId = values.vehicle_id || null;
      const driverId = values.driver_id || null;
      const nextStatus = shouldDemoteToScheduled(vehicleId, driverId)
        ? 'scheduled'
        : resolveTripStatusAfterAssign(vehicleId, driverId);
      const specialNotes =
        (typeof values.special_notes === 'string' ? values.special_notes : '').trim() || null;

      const { error } = await supabase
        .from('trips')
        .update({
          vehicle_id: vehicleId,
          driver_id: driverId,
          fleet_operator_id: values.fleet_operator_id,
          status: nextStatus,
          special_notes: specialNotes,
          attachment_urls: attachmentUrls,
        })
        .eq('id', assignTrip.id);

      if (error) {
        const parsed = parseComplianceDispatchError(error.message || '');
        if (parsed.kind === 'DRIVER_LICENSE_EXPIRED') {
          message.error(
            t('trips.messages.driverLicenseExpired', {
              date: parsed.expiry,
              tripDate: parsed.onDate || tripDate,
            })
          );
        } else if (parsed.kind === 'VEHICLE_REGISTRATION_EXPIRED') {
          message.error(
            t('trips.messages.vehicleRegistrationExpired', {
              date: parsed.expiry,
              tripDate: parsed.onDate || tripDate,
            })
          );
        } else {
          message.error(t('trips.messages.assignmentFailed'));
        }
      } else {
        message.success(
          nextStatus === 'dispatched'
            ? t('trips.messages.assignedDispatched')
            : t('trips.messages.assignmentSaved')
        );
        if (detailTrip?.id === assignTrip.id) {
          setDetailTrip((prev) =>
            prev
              ? {
                  ...prev,
                  vehicle_id: values.vehicle_id || undefined,
                  driver_id: values.driver_id || undefined,
                  status: nextStatus,
                  special_notes: specialNotes ?? undefined,
                  attachment_urls: attachmentUrls,
                  vehicles: vehicle
                    ? { fleet_number: vehicle.fleet_number, model: vehicle.model }
                    : undefined,
                  drivers: driver ? { name: driver.name } : undefined,
                }
              : prev
          );
          setDetailStatus(nextStatus);
        }
        setAssignModalVisible(false);
        setAssignAttachmentFileList([]);
        setAssignExistingPaths([]);
        await afterMutation(assignTrip.id, fetchTrips);
      }
    } catch (err: unknown) {
      message.error(t('trips.messages.assignmentFailed'));
    } finally {
      setAssignSaving(false);
    }
  };

  const handleDetailStatusChange = async (newStatus: TripStatus) => {
    if (!detailTrip) return;

    if (!canTransition(detailTrip.status as TripStatus, newStatus)) {
      message.error(t('trips.messages.invalidStatusTransition'));
      return;
    }

    if (newStatus === 'dispatched' && (!detailTrip.vehicle_id || !detailTrip.driver_id)) {
      message.error(t('trips.messages.dispatchNeedsAssign'));
      return;
    }

    if (newStatus === 'cancelled') {
      if (!cancelReason.trim()) {
        message.error(t('trips.messages.cancellationReasonRequired'));
        return;
      }

      const { error } = await supabase
        .from('trips')
        .update({ status: 'cancelled', cancelled_reason: cancelReason })
        .eq('id', detailTrip.id);

      if (error) {
        message.error(t('trips.messages.saveFailed'));
      } else {
        message.success(t('trips.messages.cancelled'));
        const tripId = detailTrip.id;
        setDetailTrip(null);
        await afterMutation(tripId, fetchTrips);
      }
      return;
    }

    const { error } = await supabase
      .from('trips')
      .update({ status: newStatus })
      .eq('id', detailTrip.id);

    if (error) {
      message.error(t('trips.messages.saveFailed'));
    } else {
      message.success(t('trips.messages.statusUpdated'));
      setDetailStatus(newStatus);
      setDetailTrip((prev) => (prev ? { ...prev, status: newStatus } : prev));
      await afterMutation(detailTrip.id, fetchTrips);
    }
  };

  const handleGenerateDocument = async (docType: 'itinerary' | 'invoice') => {
    if (!detailTrip) return;

    const setLoading = docType === 'itinerary' ? setGeneratingItinerary : setGeneratingInvoice;
    setLoading(true);
    try {
      const result = await generateTripDocument(detailTrip.id, docType);
      window.open(result.signedUrl, '_blank');
      message.success(
        t('trips.messages.documentGenerated', {
          document: t(`trips.documentNames.${docType}`),
        })
      );
    } catch (err: unknown) {
      message.error(t('trips.messages.documentFailed'));
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters = !!(
    dateRange ||
    driverFilter ||
    orgFilter ||
    fleetFilter ||
    statusFilter
  );
  const clearFilters = () => {
    setDateRange(null);
    setDriverFilter(undefined);
    setOrgFilter(undefined);
    setFleetFilter(undefined);
    setStatusFilter(undefined);
    const params = new URLSearchParams(searchParams);
    params.delete('date');
    setSearchParams(params, { replace: true });
  };

  const showOwnerStatusColumn = useMemo(
    () => trips.some((trip) => trip.source === 'platform'),
    [trips]
  );

  const tripsPinColumns = useMemo(() => {
    const list = [
      { key: 'trip_number', label: t('trips.columns.tripNumber') },
      { key: 'job_number', label: t('trips.columns.jobNumber') },
      { key: 'schedule', label: t('trips.columns.schedule') },
      { key: 'organization', label: t('trips.columns.organization') },
      ...(isOwnerMode ? [] : [{ key: 'fleet', label: t('trips.columns.fleet') }]),
      { key: 'vehicleDriver', label: t('trips.columns.vehicleDriver') },
      { key: 'route', label: t('trips.columns.route') },
      { key: 'passenger_count', label: t('trips.columns.passengers') },
      { key: 'driver_full_day_escort', label: t('trips.columns.fullDayEscort') },
      { key: 'status', label: t('trips.columns.status') },
      ...(showOwnerStatusColumn
        ? [{ key: 'assignment_status', label: t('trips.columns.ownerStatus') }]
        : []),
      { key: 'actions', label: t('trips.columns.actions') },
    ];
    return list;
  }, [isOwnerMode, showOwnerStatusColumn, t]);

  const { resolveFixed, settingsButton } = useTableColumnPins(
    'ubus.admin.trips.columnPins.v1',
    tripsPinColumns,
    TRIPS_COLUMN_PIN_DEFAULTS,
  );

  const sourceShortLabel = useCallback(
    (source?: Trip['source'] | null) => {
      if (source === 'owner') return t('trips.sourceShort.owner');
      if (source === 'platform') return t('trips.sourceShort.platform');
      return t('trips.sourceShort.hq');
    },
    [t]
  );

  const { columns, scrollX } = useMemo(() => {
    const cols: TableProps<TripWithRelations>['columns'] = [
      {
        title: t('trips.columns.tripNumber'),
        dataIndex: 'trip_number',
        key: 'trip_number',
        width: 140,
        fixed: resolveFixed('trip_number'),
        ellipsis: true,
        render: (v: string | undefined) => v || '—',
      },
      {
        title: t('trips.columns.jobNumber'),
        key: 'job_number',
        width: 140,
        fixed: resolveFixed('job_number'),
        ellipsis: true,
        render: (_, record) => {
          const jobNumber = record.jobs?.job_number;
          const jobId = record.jobs?.id ?? record.job_id;
          if (!jobNumber || !jobId) return '—';
          return (
            <Link
              to={`/jobs/${encodeURIComponent(jobId)}`}
              onClick={(e) => e.stopPropagation()}
            >
              {jobNumber}
            </Link>
          );
        },
      },
      {
        title: t('trips.columns.schedule'),
        key: 'schedule',
        width: 128,
        fixed: resolveFixed('schedule'),
        sorter: (a, b) => (a.date || '').localeCompare(b.date || ''),
        render: (_, record) => {
          const pickup = record.pickup_time ? formatTime(record.pickup_time) : '—';
          const dropoffEta = record.estimated_dropoff_time
            ? formatTime(record.estimated_dropoff_time)
            : null;
          const end = record.end_time ? formatTime(record.end_time) : '—';
          const driveMin = record.route_drive_minutes;
          const source = record.route_drive_source;
          const sourceKey = routeDriveSourceI18nKey(source);
          const km = formatRouteDriveKm(record.route_drive_distance_meters);
          const kmSuffix = km ? t('trips.routeDriveKmSuffix', { km }) : '';
          return (
            <div>
              <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{formatDate(record.date)}</div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {dropoffEta
                  ? t('trips.schedulePickupDropoff', { pickup, dropoff: dropoffEta })
                  : `${pickup}–${end}`}
              </Text>
              {typeof driveMin === 'number' && sourceKey ? (
                <Tag
                  color={routeDriveSourceTagColor(source)}
                  style={{ marginTop: 4, fontSize: 11 }}
                >
                  {t(`trips.${sourceKey}`, { minutes: driveMin, kmSuffix })}
                </Tag>
              ) : null}
            </div>
          );
        },
      },
      {
        title: t('trips.columns.organization'),
        key: 'organization',
        width: 180,
        fixed: resolveFixed('organization'),
        ellipsis: true,
        render: (_, record) => {
          const name = record.organizations?.name || '—';
          return (
            <Tooltip title={name === '—' ? undefined : name}>
              <span
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: t('trips.columns.fleet'),
        key: 'fleet',
        width: 140,
        fixed: resolveFixed('fleet'),
        ellipsis: true,
        render: (_, record) => {
          const name =
            record.fleet_operators?.name ||
            fleetOperators.find((f) => f.id === record.fleet_operator_id)?.name ||
            '—';
          return (
            <Tooltip title={name === '—' ? undefined : name}>
              <span
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: t('trips.columns.vehicleDriver'),
        key: 'vehicleDriver',
        width: 150,
        fixed: resolveFixed('vehicleDriver'),
        ellipsis: true,
        render: (_, record) => {
          const vehicle = record.vehicles
            ? `${record.vehicles.fleet_number} · ${record.vehicles.model}`
            : '—';
          const driver = record.drivers?.name;
          return (
            <Tooltip title={`${vehicle}${driver ? ` / ${driver}` : ''}`}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {vehicle}
                </div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {driver || t('trips.unassignedDriver')}
                </Text>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: t('trips.columns.route'),
        key: 'route',
        width: 260,
        fixed: resolveFixed('route'),
        ellipsis: true,
        render: (_, record) => {
          const pickup = record.pickup_address?.trim() || '—';
          const dropoff = record.dropoff_address?.trim() || '—';
          const label = `${pickup} → ${dropoff}`;
          return (
            <Tooltip title={label}>
              <span
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: t('trips.columns.passengers'),
        dataIndex: 'passenger_count',
        key: 'passenger_count',
        width: 72,
        fixed: resolveFixed('passenger_count'),
        align: 'right',
      },
      {
        title: t('trips.columns.fullDayEscort'),
        dataIndex: 'driver_full_day_escort',
        key: 'driver_full_day_escort',
        width: 120,
        fixed: resolveFixed('driver_full_day_escort'),
        filters: [
          { text: t('trips.fullDayEscortYes'), value: true },
          { text: t('trips.fullDayEscortNo'), value: false },
        ],
        onFilter: (value, record) => Boolean(record.driver_full_day_escort) === value,
        render: (v: boolean | undefined) =>
          v ? (
            <Tag color="purple">{t('trips.fullDayEscortYes')}</Tag>
          ) : (
            <Text type="secondary">{t('trips.fullDayEscortNo')}</Text>
          ),
      },
      {
        title: t('trips.columns.status'),
        dataIndex: 'status',
        key: 'status',
        width: 120,
        fixed: resolveFixed('status'),
        render: (status: string, record) => {
          const missingCoords = !(record.pickup_geopoint && record.dropoff_geopoint);
          return (
            <div>
              <Space size={4} wrap={false}>
                <StatusTag status={status} />
                {missingCoords ? (
                  <Tooltip title={t('trips.missingCoords')}>
                    <WarningOutlined
                      style={{ color: '#d48806' }}
                      aria-label={t('trips.missingCoords')}
                    />
                  </Tooltip>
                ) : null}
              </Space>
              <div>
                <Tooltip title={formatTripSourceLabel(record.source)}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {sourceShortLabel(record.source)}
                  </Text>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
    ];

    if (showOwnerStatusColumn) {
      cols.push({
        title: t('trips.columns.ownerStatus'),
        dataIndex: 'assignment_status',
        key: 'assignment_status',
        width: 110,
        fixed: resolveFixed('assignment_status'),
        render: (val: string, record) => {
          if (record.source !== 'platform') return '—';
          if (!formatAssignmentStatusLabel(val as Trip['assignment_status'])) return '—';
          return <StatusTag status={val} />;
        },
      });
    }

    cols.push({
      title: t('trips.columns.actions'),
      key: 'actions',
      width: 200,
      fixed: resolveFixed('actions'),
      render: (_, record) => {
        // Allow reassign/clear crew on scheduled or dispatched (IA §5 rule 6)
        const canAssign =
          canEditTrips &&
          (record.status === 'scheduled' || record.status === 'dispatched') &&
          (record.source !== 'platform' ||
            (isOwnerMode && record.assignment_status === 'accepted'));
        const canAssignOwner =
          !isOwnerMode &&
          canEditTrips &&
          record.status === 'scheduled' &&
          record.source === 'platform' &&
          (record.assignment_status === 'n/a' || record.assignment_status === 'rejected');
        const canMutate = canEditTrips && canEditTrip(record.status as TripStatus);
        const canDeleteRow =
          canMutate && (!isOwnerMode || record.source === 'owner');

        const menuItems: MenuProps['items'] = [
          {
            key: 'gps',
            icon: <EnvironmentOutlined />,
            label: t('trips.gps'),
            onClick: () => navigate(`/trips/${record.id}/map`),
          },
        ];
        if (canMutate) {
          menuItems.push({
            key: 'edit',
            icon: <EditOutlined />,
            label: t('trips.edit'),
            onClick: () => handleEditOrView(record),
          });
        }
        if (canAssignOwner) {
          menuItems.push({
            key: 'assignOwner',
            icon: <UserAddOutlined />,
            label: t('trips.assignOwner'),
            onClick: () => handleAssignToOwner(record),
          });
        }
        if (canDeleteRow) {
          menuItems.push({ type: 'divider' });
          menuItems.push({
            key: 'delete',
            icon: <DeleteOutlined />,
            label: t('trips.delete'),
            danger: true,
            onClick: () => handleDelete(record.id),
          });
        }

        return (
          <TableRowActions wrap={false}>
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
              {t('trips.view')}
            </Button>
            {canAssign ? (
              <Button
                type="link"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => handleAssign(record)}
              >
                {t('trips.assign')}
              </Button>
            ) : null}
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button
                type="link"
                size="small"
                icon={<MoreOutlined />}
                aria-label={t('trips.more')}
              />
            </Dropdown>
          </TableRowActions>
        );
      },
    });

    const visible = isOwnerMode
      ? cols.filter((c) => (c as { key?: string }).key !== 'fleet')
      : cols;

    const totalWidth = visible.reduce((sum, col) => {
      const w = (col as { width?: number }).width;
      return sum + (typeof w === 'number' ? w : 160);
    }, 0);

    return { columns: visible, scrollX: Math.max(totalWidth, 1200) };
  }, [
    canEditTrips,
    fleetOperators,
    isOwnerMode,
    navigate,
    resolveFixed,
    showOwnerStatusColumn,
    sourceShortLabel,
    t,
    // handlers used inside actions render — stable enough via closure; listed for eslint
    handleAssign,
    handleAssignToOwner,
    handleDelete,
    handleEditOrView,
    openDetail,
    formatAssignmentStatusLabel,
    formatTripSourceLabel,
  ]);

  const detailAllowedStatuses = detailTrip
    ? getAllowedNextStatuses(detailTrip.status as TripStatus)
    : [];

  const detailCanAssign =
    !!detailTrip &&
    canEditTrips &&
    (detailTrip.status === 'scheduled' || detailTrip.status === 'dispatched') &&
    (detailTrip.source !== 'platform' ||
      (isOwnerMode && detailTrip.assignment_status === 'accepted'));
  const detailCanAssignOwner =
    !isOwnerMode &&
    !!detailTrip &&
    canEditTrips &&
    detailTrip.status === 'scheduled' &&
    detailTrip.source === 'platform' &&
    (detailTrip.assignment_status === 'n/a' || detailTrip.assignment_status === 'rejected');
  const detailMissingVehicle = !!detailTrip && !detailTrip.vehicle_id;
  const detailMissingDriver = !!detailTrip && !detailTrip.driver_id;
  const detailNeedsAssign = detailMissingVehicle || detailMissingDriver;

  const detailDocMenuItems: MenuProps['items'] = [
    {
      key: 'itinerary',
      icon: <FileTextOutlined />,
      label: t('trips.generateItinerary'),
      disabled: generatingItinerary,
      onClick: () => void handleGenerateDocument('itinerary'),
    },
    {
      key: 'invoice',
      icon: <FileTextOutlined />,
      label: t('trips.generateInvoice'),
      disabled: generatingInvoice,
      onClick: () => void handleGenerateDocument('invoice'),
    },
  ];

  const statusActionLabel = (status: TripStatus) => {
    const key = `trips.statusAction.${status}` as const;
    const translated = t(key);
    return translated === key ? status.replace('_', ' ') : translated;
  };

  return (
    <>
      <AdminListPage
        title={t('trips.title')}
        subtitle={t('trips.subtitle')}
        resultCount={trips.length}
        extra={
          <Space>
            <Button type="link" onClick={() => navigate('/jobs')}>
              {t('trips.jobs')}
            </Button>
            {canOpenDailySchedule ? (
              <Button type="link" onClick={() => navigate('/daily-schedule')}>
                {t('trips.dailySchedule')}
              </Button>
            ) : null}
            {canEditTrips ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                {t('trips.addTrip')}
              </Button>
            ) : null}
          </Space>
        }
        filters={
          <>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                const next = dates as [Dayjs, Dayjs] | null;
                setDateRange(next);
                const params = new URLSearchParams(searchParams);
                if (next?.[0] && next?.[1] && next[0].isSame(next[1], 'day')) {
                  params.set('date', next[0].format('YYYY-MM-DD'));
                } else {
                  params.delete('date');
                }
                setSearchParams(params, { replace: true });
              }}
              allowClear
            />
            <Select
              placeholder={t('trips.allDrivers')}
              allowClear
              style={{ minWidth: 160 }}
              value={driverFilter}
              onChange={setDriverFilter}
              showSearch
              optionFilterProp="label"
              options={drivers.map((d) => ({ value: d.id, label: d.name }))}
            />
            <Select
              placeholder={t('trips.allOrganizations')}
              allowClear
              style={{ minWidth: 180 }}
              value={orgFilter}
              onChange={setOrgFilter}
              showSearch
              optionFilterProp="label"
              options={organizations.map((o) => ({ value: o.id, label: o.name }))}
            />
            {!isOwnerMode ? (
            <Select
              placeholder={t('trips.allFleets')}
              allowClear
              style={{ minWidth: 160 }}
              value={fleetFilter}
              onChange={setFleetFilter}
              showSearch
              optionFilterProp="label"
              options={fleetOperators.map((f) => ({ value: f.id, label: f.name }))}
            />
            ) : null}
            <Select
              placeholder={t('trips.allStatuses')}
              allowClear
              style={{ minWidth: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'scheduled', label: t('statuses.scheduled') },
                { value: 'dispatched', label: t('statuses.dispatched') },
                { value: 'in_progress', label: t('statuses.in_progress') },
                { value: 'completed', label: t('statuses.completed') },
                { value: 'cancelled', label: t('statuses.cancelled') },
              ]}
            />
          </>
        }
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        toolbarExtra={settingsButton}
      >
        {missingGeopointCount > 0 && !isOwnerMode ? (
          <Alert
            type="warning"
            showIcon
            icon={<AimOutlined />}
            style={{ marginBottom: 16 }}
            message={t('trips.geocodeAlert.message', { count: missingGeopointCount })}
            description={t('trips.geocodeAlert.description')}
            action={
              <Button
                size="small"
                loading={backfillingGeopoints}
                onClick={() => void handleBackfillGeopoints()}
              >
                {t('trips.geocodeAlert.action')}
              </Button>
            }
          />
        ) : null}
        <Table
          className="admin-trips-table"
          dataSource={trips}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={onTableChange}
          scroll={{ x: scrollX }}
          rowClassName={(record) =>
            [
              highlightedIds.has(record.id) ? 'trip-row-highlight' : '',
              stabilityRowClassName(record, 0),
            ]
              .filter(Boolean)
              .join(' ')
          }
          locale={{ emptyText: t('trips.empty') }}
        />
      </AdminListPage>

      <Modal
        title={editingTrip ? t('trips.editTrip') : t('trips.addTrip')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={savingTrip}
        width={800}
        destroyOnClose
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <Form
          key={i18n.language}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="org_id"
            label={t('trips.form.organization')}
          >
            <Select
              placeholder={t('trips.placeholders.selectOrganization')}
              allowClear
              showSearch
              optionFilterProp="label"
              options={organizations.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
              onChange={async (orgId: string | undefined) => {
                if (!orgId) return;
                const person = form.getFieldValue('contact_person');
                const phone = form.getFieldValue('contact_phone');
                if (person || phone) return;
                const { data } = await supabase
                  .from('organizations')
                  .select('contact_person, contact_phone')
                  .eq('id', orgId)
                  .maybeSingle();
                if (!data) return;
                form.setFieldsValue({
                  contact_person: data.contact_person || undefined,
                  contact_phone: data.contact_phone || undefined,
                });
              }}
            />
          </Form.Item>
          <Space size="large" style={{ display: 'flex' }} align="start" wrap>
            <Form.Item
              name="contact_person"
              label={t('trips.form.contactPerson')}
              style={{ flex: 1, minWidth: 180 }}
              rules={[{ required: true, whitespace: true, message: t('trips.validation.contactPerson') }]}
            >
              <Input placeholder={t('trips.placeholders.contactPerson')} allowClear />
            </Form.Item>
            <Form.Item
              name="contact_phone"
              label={t('trips.form.contactPhone')}
              style={{ flex: 1, minWidth: 180 }}
              rules={[{ required: true, whitespace: true, message: t('trips.validation.contactPhone') }]}
            >
              <Input placeholder={t('trips.placeholders.contactPhone')} allowClear />
            </Form.Item>
          </Space>
          <Form.Item
            name="date"
            label={t('trips.form.date')}
            rules={[{ required: true, message: t('trips.validation.date') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('trips.form.selectDate')}
            />
          </Form.Item>
          <Form.Item
            name="pickup_time"
            label={t('trips.form.pickupTime')}
            rules={[{ required: true, message: t('trips.validation.pickupTime') }]}
          >
            <TimePicker
              style={{ width: '100%' }}
              format="HH:mm"
              placeholder={t('trips.form.selectTime')}
              onChange={(value) => {
                if (value) {
                  const pickup = value.format('HH:mm:ss');
                  form.setFieldValue(
                    'end_time',
                    dayjs(defaultEndTimeFromPickup(pickup), 'HH:mm:ss')
                  );
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="end_time"
            label={t('trips.form.endTime')}
            rules={[{ required: true, message: t('trips.validation.endTime') }]}
            extra={t('trips.form.endTimeExtra')}
          >
            <TimePicker
              style={{ width: '100%' }}
              format="HH:mm"
              placeholder={t('trips.form.selectTime')}
            />
          </Form.Item>
          <Form.Item
            name="pickup_address"
            label={t('trips.form.pickupAddress')}
            extra={t('trips.form.pickupAddressExtra')}
            rules={[{ required: true, message: t('trips.validation.pickupAddress') }]}
          >
            <AddressField
              placeholder={t('trips.placeholders.pickupAddress')}
              coordinates={pickupCoords}
              onCoordinatesChange={setPickupCoords}
            />
          </Form.Item>
          <Form.Item
            name="dropoff_address"
            label={t('trips.form.dropoffAddress')}
            extra={t('trips.form.dropoffAddressExtra')}
            rules={[{ required: true, message: t('trips.validation.dropoffAddress') }]}
          >
            <AddressField
              placeholder={t('trips.placeholders.dropoffAddress')}
              coordinates={dropoffCoords}
              onCoordinatesChange={setDropoffCoords}
            />
          </Form.Item>
          <Form.Item
            name="passenger_count"
            label={t('trips.form.passengerCount')}
            rules={[{ required: true, message: t('trips.validation.passengerCount') }]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder={t('trips.placeholders.passengerCount')}
            />
          </Form.Item>
          <Form.Item
            name="driver_full_day_escort"
            label={t('trips.form.fullDayEscort')}
            valuePropName="checked"
            extra={t('trips.form.fullDayEscortHint')}
          >
            <Switch />
          </Form.Item>
          <Form.Item name="special_notes" label={t('trips.form.specialNotes')}>
            <Input.TextArea rows={3} placeholder={t('trips.placeholders.specialNotes')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('trips.assignVehicleDriver')}
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setAssignAttachmentFileList([]);
          setAssignExistingPaths([]);
        }}
        onOk={() => assignForm.submit()}
        confirmLoading={assignSaving}
        width={640}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignSubmit}>
          {!isOwnerMode ? (
          <Form.Item
            name="fleet_operator_id"
            label={t('trips.form.fleet')}
            rules={[{ required: true, message: t('trips.validation.fleetRequired') }]}
          >
            <Select
              placeholder={t('trips.placeholders.selectFleet')}
              showSearch
              optionFilterProp="label"
              options={fleetOperators.map((f) => ({
                value: f.id,
                label: f.name,
              }))}
              onChange={() => {
                assignForm.setFieldsValue({
                  vehicle_id: undefined,
                  driver_id: undefined,
                });
              }}
            />
          </Form.Item>
          ) : (
            <Form.Item name="fleet_operator_id" hidden>
              <Input />
            </Form.Item>
          )}
          {!isOwnerMode ? (
          <DispatchRecommendationList
            tripId={assignTrip?.id}
            fleetOperatorId={assignFleetOperatorId}
            allowedVehicleIds={assignAllowedVehicleIds}
            onSelect={(vehicleId, driverId) => {
              const vehicle = vehicles.find((v) => v.id === vehicleId);
              assignForm.setFieldsValue({
                fleet_operator_id:
                  vehicle?.fleet_operator_id || assignFleetOperatorId || undefined,
                vehicle_id: vehicleId,
                driver_id: driverId,
              });
            }}
          />
          ) : null}
          <Form.Item name="vehicle_id" label={t('trips.form.vehicle')}>
            <Select
              placeholder={
                assignFleetOperatorId
                  ? t('trips.placeholders.selectVehicle')
                  : t('trips.placeholders.selectFleetFirst')
              }
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!assignFleetOperatorId}
              options={assignFleetVehicles
                .filter(
                  (v) =>
                    !assignTrip ||
                    !isDispatchBlockedByExpiry(v.registration_expiry, assignTrip.date)
                )
                .map((v) => ({
                  value: v.id,
                  label: `${v.fleet_number} - ${v.model}`,
                }))}
              onChange={(vehicleId) => {
                const nextVehicle = assignFleetVehicles.find((v) => v.id === vehicleId);
                const preferred = nextVehicle?.preferred_driver_id;
                const preferredInFleet =
                  preferred &&
                  assignFleetDrivers.some((d) => d.id === preferred)
                    ? preferred
                    : undefined;
                const currentDriver = assignForm.getFieldValue('driver_id') as
                  | string
                  | undefined;
                const currentInFleet =
                  currentDriver &&
                  assignFleetDrivers.some((d) => d.id === currentDriver)
                    ? currentDriver
                    : undefined;
                assignForm.setFieldsValue({
                  vehicle_id: vehicleId,
                  driver_id: currentInFleet || preferredInFleet || undefined,
                });
              }}
            />
          </Form.Item>
          <Form.Item name="driver_id" label={t('trips.form.driver')}>
            <Select
              placeholder={
                assignFleetOperatorId
                  ? t('trips.placeholders.selectDriver')
                  : t('trips.placeholders.selectFleetFirst')
              }
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!assignFleetOperatorId}
              options={assignFleetDrivers
                .filter(
                  (d) =>
                    !assignTrip ||
                    !isDispatchBlockedByExpiry(d.license_expiry, assignTrip.date)
                )
                .map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
            />
          </Form.Item>
          <Form.Item
            name="special_notes"
            label={t('trips.form.assignNotes')}
            extra={t('trips.form.assignNotesHint')}
          >
            <Input.TextArea rows={4} placeholder={t('trips.placeholders.assignNotes')} />
          </Form.Item>
          <Form.Item label={t('trips.form.attachments')} extra={t('trips.form.attachmentsHint')}>
            <Upload
              listType="picture-card"
              fileList={assignAttachmentFileList}
              accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
              disabled={pdfConverting}
              beforeUpload={(file) => {
                void handleAssignAttachmentPick(file);
                return Upload.LIST_IGNORE;
              }}
              onChange={({ fileList }) => {
                // Only allow removals / reorders of already-added items (edit modal adds files)
                setAssignAttachmentFileList(
                  fileList.filter((f) => f.originFileObj || String(f.uid).startsWith('existing-')).slice(0, 9)
                );
              }}
              onPreview={(file) => {
                if (file.url || file.thumbUrl) {
                  window.open(file.url || file.thumbUrl, '_blank');
                }
              }}
            >
              {assignAttachmentFileList.length >= 9 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t('trips.form.upload')}</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Text type="secondary">{t('trips.form.assignAutoDispatchHint')}</Text>
        </Form>
      </Modal>

      <ImageEditModal
        open={imageEditOpen}
        file={imageEditFile}
        onCancel={cancelImageEditQueue}
        onConfirm={(edited) => {
          const uid = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const preview = URL.createObjectURL(edited);
          setAssignAttachmentFileList((prev) => {
            if (prev.length >= 9) return prev;
            return [
              ...prev,
              {
                uid,
                name: edited.name,
                status: 'done',
                originFileObj: edited as UploadFile['originFileObj'],
                url: preview,
                thumbUrl: preview,
              },
            ];
          });
          advanceImageEditQueue();
        }}
      />

      <Modal
        title={t('trips.assignFleetOwner')}
        open={ownerAssignModalVisible}
        onCancel={() => {
          setOwnerAssignModalVisible(false);
          setOwnerAssignTrip(null);
        }}
        footer={null}
        width={720}
      >
        <FleetOperatorRecommendationList
          tripId={ownerAssignTrip?.id}
          passengerCount={ownerAssignTrip?.passenger_count}
          onSelect={(rec) => void handleOwnerAssignSelect(rec)}
        />
      </Modal>

      <EntityDrawer
        open={!!detailTrip}
        onClose={() => setDetailTrip(null)}
        title={detailTrip ? formatTripBrief(detailTrip) : t('trips.details')}
        width={640}
        extra={
          detailTrip && (
            <Space wrap size={8}>
              {detailCanAssign ? (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => handleAssign(detailTrip)}
                >
                  {t('trips.assign')}
                </Button>
              ) : null}
              {detailCanAssignOwner ? (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => handleAssignToOwner(detailTrip)}
                >
                  {t('trips.assignOwner')}
                </Button>
              ) : null}
              <Button
                icon={<EnvironmentOutlined />}
                onClick={() => navigate(`/trips/${detailTrip.id}/map`)}
              >
                {t('trips.gpsMap')}
              </Button>
              <Dropdown menu={{ items: detailDocMenuItems }} trigger={['click']}>
                <Button
                  icon={<FileTextOutlined />}
                  loading={generatingItinerary || generatingInvoice}
                >
                  {t('trips.documents')}
                </Button>
              </Dropdown>
            </Space>
          )
        }
      >
        {detailTrip && (
          <>
            {detailTrip.status === 'scheduled' && detailNeedsAssign ? (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 16 }}
                message={
                  detailMissingDriver
                    ? t('trips.driverMissingAlert')
                    : t('trips.vehicleMissingAlert')
                }
                action={
                  detailCanAssign ? (
                    <Button size="small" type="primary" onClick={() => handleAssign(detailTrip)}>
                      {t('trips.assign')}
                    </Button>
                  ) : detailCanAssignOwner ? (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleAssignToOwner(detailTrip)}
                    >
                      {t('trips.assignOwner')}
                    </Button>
                  ) : undefined
                }
              />
            ) : null}

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('trips.fields.date')}>
                {formatDate(detailTrip.date)}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.tripNumber')}>
                {detailTrip.trip_number || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.jobNumber')}>
                {detailTrip.jobs?.job_number && (detailTrip.jobs?.id || detailTrip.job_id) ? (
                  <Link
                    to={`/jobs/${encodeURIComponent(detailTrip.jobs?.id ?? detailTrip.job_id!)}`}
                  >
                    {detailTrip.jobs.job_number}
                  </Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.pickupTime')}>
                {detailTrip.pickup_time ? formatTime(detailTrip.pickup_time) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.estimatedDropoff')}>
                {detailTrip.estimated_dropoff_time
                  ? formatTime(detailTrip.estimated_dropoff_time)
                  : t('trips.estimatedDropoffPending')}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.routeDrive')}>
                {typeof detailTrip.route_drive_minutes === 'number' &&
                routeDriveSourceI18nKey(detailTrip.route_drive_source) ? (
                  <Tag color={routeDriveSourceTagColor(detailTrip.route_drive_source)}>
                    {t(`trips.${routeDriveSourceI18nKey(detailTrip.route_drive_source)}`, {
                      minutes: detailTrip.route_drive_minutes,
                      kmSuffix: formatRouteDriveKm(detailTrip.route_drive_distance_meters)
                        ? t('trips.routeDriveKmSuffix', {
                            km: formatRouteDriveKm(detailTrip.route_drive_distance_meters),
                          })
                        : '',
                    })}
                  </Tag>
                ) : (
                  t('trips.routeDriveUnavailable')
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.endTimeOccupancy')}>
                {detailTrip.end_time ? formatTime(detailTrip.end_time) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.actualStart')}>
                {detailTrip.started_at ? formatDateTime(detailTrip.started_at) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.actualComplete')}>
                {detailTrip.completed_at ? formatDateTime(detailTrip.completed_at) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.organization')}>
                {detailTrip.organizations?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.contactPerson')}>
                {detailTrip.contact_person || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.contactPhone')}>
                {detailTrip.contact_phone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.fleet')}>
                {detailTrip.fleet_operators?.name ||
                  fleetOperators.find((f) => f.id === detailTrip.fleet_operator_id)?.name ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.vehicle')}>
                {detailTrip.vehicles ? (
                  `${detailTrip.vehicles.fleet_number} (${detailTrip.vehicles.model})`
                ) : (
                  <Tag color="warning">{t('trips.assignBeforeDispatch')}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.driver')}>
                {detailTrip.drivers?.name ? (
                  detailTrip.drivers.name
                ) : (
                  <Tag color="warning" icon={<WarningOutlined />}>
                    {t('trips.unassignedDriver')}
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.pickup')}>
                {detailTrip.pickup_address}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.dropoff')}>
                {detailTrip.dropoff_address}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.mapCoordinates')}>
                {detailTrip.pickup_geopoint && detailTrip.dropoff_geopoint ? (
                  <Tag color="success">{t('trips.coordsVerified')}</Tag>
                ) : (
                  <Tag color="warning">{t('trips.coordsMissing')}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('trips.fields.passengers')}>
                {t('trips.passengersCount', { count: detailTrip.passenger_count ?? 0 })}
              </Descriptions.Item>
              {detailTrip.special_notes ? (
                <Descriptions.Item label={t('trips.fields.specialNotes')}>
                  {detailTrip.special_notes}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t('trips.fields.status')}>
                <StatusTag status={detailTrip.status} />
                {detailTrip.driver_full_day_escort ? (
                  <Tag color="purple">{t('trips.fullDayEscort')}</Tag>
                ) : null}
              </Descriptions.Item>
              {detailTrip.status === 'cancelled' || detailTrip.cancelled_reason || detailTrip.cancelled_at ? (
                <>
                  <Descriptions.Item label={t('trips.fields.cancellationReason')}>
                    {detailTrip.cancelled_reason || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('trips.fields.cancelledAt')}>
                    {detailTrip.cancelled_at ? formatDateTime(detailTrip.cancelled_at) : '—'}
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>

            {detailAllowedStatuses.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>{t('trips.updateStatus')}</Text>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {detailAllowedStatuses.map((status) => {
                    const dispatchBlocked =
                      status === 'dispatched' && detailNeedsAssign;
                    return (
                      <Tooltip
                        key={status}
                        title={dispatchBlocked ? t('trips.assignBeforeDispatch') : undefined}
                      >
                        <Button
                          size="small"
                          type={status === 'dispatched' && !dispatchBlocked ? 'primary' : 'default'}
                          danger={status === 'cancelled'}
                          disabled={dispatchBlocked}
                          onClick={() => {
                            if (status === 'cancelled') {
                              setDetailStatus('cancelled');
                            } else {
                              void handleDetailStatusChange(status);
                            }
                          }}
                        >
                          {statusActionLabel(status)}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </div>
                {detailStatus === 'cancelled' && canCancelTrip(detailTrip.status as TripStatus) && (
                  <div style={{ marginTop: 12 }}>
                    <Input.TextArea
                      rows={2}
                      placeholder={t('trips.placeholders.cancellationReason')}
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                    />
                    <Button
                      type="primary"
                      danger
                      style={{ marginTop: 8 }}
                      onClick={() => void handleDetailStatusChange('cancelled')}
                    >
                      {t('trips.confirmCancel')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <Text strong>{t('trips.feedback.title')}</Text>
              <Table
                style={{ marginTop: 8 }}
                size="small"
                rowKey="id"
                loading={detailFeedbackLoading}
                dataSource={detailFeedback}
                pagination={false}
                locale={{ emptyText: t('trips.feedback.empty') }}
                columns={[
                  {
                    title: t('trips.feedback.date'),
                    dataIndex: 'created_at',
                    key: 'created_at',
                    width: 140,
                    render: (v: string) => formatDateTime(v),
                  },
                  {
                    title: t('trips.feedback.rating'),
                    dataIndex: 'rating',
                    key: 'rating',
                    width: 120,
                    render: (v: number) => <Rate disabled value={v} style={{ fontSize: 14 }} />,
                  },
                  {
                    title: t('trips.feedback.category'),
                    dataIndex: 'category',
                    key: 'category',
                    width: 100,
                    render: (v: string) => <Tag>{v}</Tag>,
                  },
                  {
                    title: t('trips.feedback.message'),
                    dataIndex: 'message',
                    key: 'message',
                    ellipsis: true,
                  },
                  {
                    title: t('trips.feedback.status'),
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: (status: string) => (
                      <StatusTag
                        status={status}
                        color={status === 'new' ? 'orange' : undefined}
                        label={status === 'new' ? t('trips.feedback.new') : undefined}
                      />
                    ),
                  },
                  {
                    title: t('trips.feedback.actions'),
                    key: 'actions',
                    width: 130,
                    render: (_: unknown, row: TripFeedback) =>
                      row.status === 'new' ? (
                        <Button
                          type="link"
                          size="small"
                          icon={<CheckCircleOutlined />}
                          onClick={() => void markDetailFeedbackReviewed(row.id)}
                        >
                          {t('trips.feedback.markReviewed')}
                        </Button>
                      ) : null,
                  },
                ]}
              />
            </div>
          </>
        )}
      </EntityDrawer>
    </>
  );
}

export default Trips;
